import { useEffect, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { Realization, authHeaders } from './realizationConstants'
import RealizationSketchMap from './RealizationSketchMap'

interface Props {
  token: string
  editing: Realization | null   // null = tworzenie
  onClose: () => void
  onSaved: () => void
}

interface ClientOpt { id: number; name: string }

export default function RealizationForm({ token, editing, onClose, onSaved }: Props) {
  const { showSuccess, showError } = useNotificationContext()
  const isEdit = !!editing
  const [clients, setClients] = useState<ClientOpt[]>([])
  const [clientId, setClientId] = useState<number | ''>(editing?.client_id ?? '')
  const [applicantName, setApplicantName] = useState(editing?.applicant_name ?? '')
  const [applicantContact, setApplicantContact] = useState(editing?.applicant_contact ?? '')
  const [location, setLocation] = useState(editing?.location ?? '')
  const [plot, setPlot] = useState(editing?.plot_number ?? '')
  const [water, setWater] = useState(!!editing?.connection_water)
  const [sewer, setSewer] = useState(!!editing?.connection_sewer)
  const [geometry, setGeometry] = useState<string | null>(editing?.geometry ?? null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetch('/api/clients/list', { headers: authHeaders(token) })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: ClientOpt[]) => {
        setClients(data)
        if (!isEdit && clientId === '' && data.length > 0) setClientId(data[0].id)
      })
      .catch(() => setClients([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    if (clientId === '') return showError('Błąd', 'Wybierz referat')
    if (!applicantName.trim()) return showError('Błąd', 'Podaj wnioskodawcę')
    if (!location.trim() || !plot.trim()) return showError('Błąd', 'Podaj miejscowość i nr działki')
    if (!water && !sewer) return showError('Błąd', 'Zaznacz co najmniej jedno medium (woda lub kanał)')
    setSaving(true)
    try {
      const body: any = {
        client_id: clientId,
        applicant_name: applicantName.trim(),
        applicant_contact: applicantContact.trim(),
        location: location.trim(),
        plot_number: plot.trim(),
        connection_water: water,
        connection_sewer: sewer,
        geometry: geometry,
      }
      const url = isEdit ? `/api/realizations/${editing!.id}` : '/api/realizations/'
      const method = isEdit ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        showSuccess(isEdit ? 'Zapisano zmiany' : 'Realizacja utworzona')
        onSaved()
      } else {
        const err = await res.json().catch(() => ({}))
        showError('Błąd', err.detail || 'Nie udało się zapisać')
      }
    } catch {
      showError('Błąd', 'Błąd połączenia')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edytuj wniosek' : 'Nowy wniosek o przyłączenie'}</h2>
          <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {!isEdit && (
            <Field label="Referat">
              <select value={clientId} onChange={(e) => setClientId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Wnioskodawca">
            <input value={applicantName} onChange={(e) => setApplicantName(e.target.value)} placeholder="Imię i nazwisko"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </Field>
          <Field label="Kontakt (telefon / e-mail)">
            <input value={applicantContact} onChange={(e) => setApplicantContact(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Miejscowość">
              <input value={location} onChange={(e) => setLocation(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </Field>
            <Field label="Nr działki">
              <input value={plot} onChange={(e) => setPlot(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Zakres przyłączenia">
            <div className="flex gap-3">
              <label className="flex-1 flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-3 cursor-pointer">
                <input type="checkbox" checked={water} onChange={(e) => setWater(e.target.checked)} className="h-5 w-5" />
                <span className="text-sm">💧 Wodociąg</span>
              </label>
              <label className="flex-1 flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-3 cursor-pointer">
                <input type="checkbox" checked={sewer} onChange={(e) => setSewer(e.target.checked)} className="h-5 w-5" />
                <span className="text-sm">🚿 Kanalizacja</span>
              </label>
            </div>
          </Field>
          {(water || sewer) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Szkic przyłącza na mapie (dołączany do wniosku)</label>
              <RealizationSketchMap value={geometry} onChange={setGeometry} water={water} sewer={sewer} />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button onClick={onClose} className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg">Anuluj</button>
          <button onClick={save} disabled={saving} className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg disabled:opacity-50">
            {saving ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Utwórz wniosek')}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  )
}
